/**
 * frontend公開設定値の読み取り(Phase「LIFF frontend実装」)。
 *
 * ここで読むのはVite標準の `import.meta.env.VITE_*` のみ(build時に静的展開
 * される値、.env.example参照)。Secretはこのファイル・このパッケージの
 * どこにも置かない(STRIPE_SECRET_KEY / LINE_CHANNEL_ACCESS_TOKEN /
 * GOOGLE_SERVICE_ACCOUNT_JSON / ADMIN_AUTH_SECRET等はbackend Workerのみが
 * 保持する)。
 */

export interface PublicConfig {
  liffId: string;
  apiBaseUrl: string;
  stripePublishableKey: string;
}

export class ConfigError extends Error {
  constructor(missingKey: string) {
    super(`Missing required public config: ${missingKey}`);
    this.name = "ConfigError";
  }
}

/**
 * `import.meta.env` 相当の値を受け取って検証する(テスト容易性のため
 * `import.meta.env` への直接依存を関数境界で切る)。
 */
export function loadConfig(env: Record<string, string | undefined>): PublicConfig {
  const liffId = env.VITE_LIFF_ID;
  const apiBaseUrl = env.VITE_API_BASE_URL;
  const stripePublishableKey = env.VITE_STRIPE_PUBLISHABLE_KEY;

  if (!liffId) throw new ConfigError("VITE_LIFF_ID");
  if (!apiBaseUrl) throw new ConfigError("VITE_API_BASE_URL");
  if (!stripePublishableKey) throw new ConfigError("VITE_STRIPE_PUBLISHABLE_KEY");

  // 末尾スラッシュを正規化し、api-client側の結合ミスを防ぐ。
  const normalizedBaseUrl = apiBaseUrl.replace(/\/+$/, "");

  return { liffId, apiBaseUrl: normalizedBaseUrl, stripePublishableKey };
}

/** 実行時(ブラウザ)用: Viteが静的展開した`import.meta.env`から読む。 */
export function loadConfigFromViteEnv(): PublicConfig {
  return loadConfig(import.meta.env as unknown as Record<string, string | undefined>);
}
